import React, { useEffect, useState, TouchEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useInsightsStore } from '../../stores/insightsStore';
import { useNavigationStore } from '../../stores/navigationStore';
import { parseReference } from '../../utils/parseReference';
import ReactMarkdown from 'react-markdown';
import { ChevronLeft, ChevronRight, Loader, X } from 'lucide-react';

export function InsightModal() {
  const { 
    isOpen,
    closeInsightModal,
    verseInfo,
    insights,
    isLoading,
    error,
    isDetailedInsight,
    setDetailedInsight,
    generateInsights,
    navigateToChapter
  } = useInsightsStore();

  const navigationStore = useNavigationStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showControls, setShowControls] = useState(false);

  if (!isOpen || !verseInfo) return null;

  const isChapterInsight = !verseInfo.verse;
  const reference = isChapterInsight 
    ? `${verseInfo.book} ${verseInfo.chapter}`
    : `${verseInfo.book} ${verseInfo.chapter}:${verseInfo.verse}`;

  // Minimum swipe distance in pixels
  const minSwipeDistance = 50;

  const onTouchStart = (e: TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setShowControls(true);
  };

  const onTouchMove = (e: TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = async () => {
    if (!touchStart || !touchEnd || !isChapterInsight || isNavigating) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (!isLeftSwipe && !isRightSwipe) return;

    setIsNavigating(true);
    if (isLeftSwipe) {
      await navigateToChapter(1); // Next chapter
    } else if (isRightSwipe) {
      await navigateToChapter(-1); // Previous chapter
    }
    setIsNavigating(false);
  };

  const handleInsightTypeChange = (detailed: boolean) => {
    setDetailedInsight(detailed);
    generateInsights();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeInsightModal();
    }
  };

  const handleVerseClick = (reference: string) => {
    const { book, chapter, verse } = parseReference(reference);
    if (book && chapter) {
      navigationStore.setPreviousLocation({
        pathname: location.pathname,
        state: {
          scrollPosition: window.scrollY,
          modalOpen: true,
          verseId: verseInfo?.verse ? `${verseInfo.book}_${verseInfo.chapter}_${verseInfo.verse}` : null
        }
      });
      
      navigate(`/bible/${book}/${chapter}`, {
        state: { verse, version: 'NIV' }
      });
      closeInsightModal();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleBackdropClick}>
      <div 
        className="bg-obsidian-900 border border-gold-500/20 rounded-lg shadow-gold w-full max-w-2xl max-h-[85vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Close Button */}
        <div className="absolute right-4 top-4 z-50">
          <button
            onClick={closeInsightModal}
            className="p-2 rounded-full hover:bg-gold-500/10 text-gold-400 hover:text-gold-300 transition-all bg-obsidian-900"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation Controls */}
        {isChapterInsight && (
          <>
            <div className="absolute left-4 top-1/2 -translate-y-1/2 z-50">
              <button
                className={`p-2 rounded-full bg-obsidian-900/80 text-gold-400 hover:text-gold-300 transition-all transform ${
                  showControls ? 'opacity-100' : 'opacity-0'
                }`}
                onClick={() => navigateToChapter(-1)}
                disabled={isNavigating}
              >
                <ChevronLeft size={24} />
              </button>
            </div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 z-50">
              <button
                className={`p-2 rounded-full bg-obsidian-900/80 text-gold-400 hover:text-gold-300 transition-all transform ${
                  showControls ? 'opacity-100' : 'opacity-0'
                }`}
                onClick={() => navigateToChapter(1)}
                disabled={isNavigating}
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </>
        )}

        {/* Content Container with Scroll */}
        <div className="overflow-y-auto min-h-0 p-8">
          {/* Header */}
          <div className="mb-6 pr-12">
            <h2 className="text-2xl font-display text-gold-500 mb-2 pr-8">
              {isChapterInsight ? 'Chapter Insights' : 'Verse Insights'} - {reference}
            </h2>
            {!isChapterInsight && verseInfo.text && (
              <div className="bg-gold-400/5 border border-gold-400/20 rounded-lg p-4 mb-6">
                <p className="text-gold-300 italic leading-relaxed tracking-wide font-medium">"{verseInfo.text}"</p>
              </div>
            )}
            {!isChapterInsight && (
              <div className="flex items-center justify-between">
                <label className="text-gray-300 text-sm block">Insight Type</label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleInsightTypeChange(false)}
                    className={`px-4 py-2 rounded transition-colors ${
                      !isDetailedInsight
                        ? 'bg-gold-400 text-obsidian-900'
                        : 'bg-gold-400/10 hover:bg-gold-400/20 text-gold-400'
                    }`}
                  >
                    Brief
                  </button>
                  <button
                    onClick={() => handleInsightTypeChange(true)}
                    className={`px-4 py-2 rounded transition-colors ${
                      isDetailedInsight
                        ? 'bg-gold-400 text-obsidian-900'
                        : 'bg-gold-400/10 hover:bg-gold-400/20 text-gold-400'
                    }`}
                  >
                    Detailed
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="prose prose-invert max-w-none">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin text-gold-400" size={32} />
              </div>
            ) : error ? (
              <div className="text-red-400 p-4 rounded bg-red-400/10 border border-red-400/20">
                {error}
              </div>
            ) : insights ? (
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => <h1 className="text-2xl font-display text-gold-400 mb-6 leading-relaxed" {...props} />,
                  h2: ({ node, ...props }) => <h2 className="text-xl font-display text-gold-400 mb-4 leading-relaxed" {...props} />,
                  h3: ({ node, ...props }) => <h3 className="text-lg font-display text-gold-400 mb-3 leading-relaxed" {...props} />,
                  p: ({ node, ...props }) => <p className="text-gray-300 mb-6 leading-relaxed tracking-wide" {...props} />,
                  ul: ({ node, ...props }) => <ul className="list-disc list-inside space-y-3 mb-6" {...props} />,
                  li: ({ node, ...props }) => <li className="text-gray-300 leading-relaxed" {...props} />,
                  a: ({ node, ...props }) => (
                    <button
                      onClick={() => handleVerseClick(props.href || '')}
                      className="text-gold-400 hover:text-gold-300 transition-colors"
                    >
                      {props.children}
                    </button>
                  ),
                }}
              >
                {insights}
              </ReactMarkdown>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
